import Autocomplete from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import type { Dayjs } from 'dayjs';
import { useAtom } from 'jotai';
import sortBy from 'lodash/sortBy';
import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import countries from '@/constants/countries.constants';
import useStyles from '@/features/TicketCreateDialog/TicketCreateDialog.styles';
import { isLayoutMobileAtom } from '@/store/layout';

const ServiceFormChangeRequestTravelNotification = () => {
  const [isLayoutMobile] = useAtom(isLayoutMobileAtom);
  const {
    control,
    formState: { errors }
  } = useFormContext<{
    custom_data: {
      travel_notification: {
        user_email: string;
        start_date: Dayjs | null;
        end_date: Dayjs | null;
        desired_action: string;
        locations: string[];
      };
    };
  }>();
  const { classes } = useStyles({ errors });
  const theme = useTheme();

  const countryCodes = useMemo(() => {
    return sortBy(Object.keys(countries), ['id']);
  }, [countries]);

  return (
    <>
      <Typography component="h2" variant="h6">
        Travel Notification
      </Typography>
      <div>
        <InputLabel htmlFor="user-email-input">User Email *</InputLabel>
        <FormControl id="user-email-input" fullWidth>
          <Controller
            control={control}
            defaultValue=""
            name="custom_data.travel_notification.user_email"
            rules={{
              required: true
            }}
            render={({ field }) => (
              <TextField
                error={!!errors.custom_data?.travel_notification?.user_email}
                {...field}
              />
            )}
          />
        </FormControl>
      </div>
      <div>
        <Stack
          direction={isLayoutMobile ? 'column' : 'row'}
          sx={{ mb: 2, gap: 2 }}
        >
          <div className={classes.ticketCreateDialogFormElement}>
            <InputLabel htmlFor="start-date-picker">Start Date *</InputLabel>
            <FormControl id="start-date-picker" fullWidth>
              <Controller
                control={control}
                defaultValue={null}
                name="custom_data.travel_notification.start_date"
                rules={{
                  required: true
                }}
                render={({ field }) => (
                  <DatePicker
                    slotProps={{
                      textField: {
                        error:
                          !!errors.custom_data?.travel_notification?.start_date
                      }
                    }}
                    {...field}
                  />
                )}
              />
            </FormControl>
          </div>
          <div className={classes.ticketCreateDialogFormElement}>
            <InputLabel htmlFor="end-date-picker">End Date *</InputLabel>
            <FormControl id="end-date-picker" fullWidth>
              <Controller
                control={control}
                defaultValue={null}
                name="custom_data.travel_notification.end_date"
                rules={{
                  required: true
                }}
                render={({ field }) => (
                  <DatePicker
                    slotProps={{
                      textField: {
                        error:
                          !!errors.custom_data?.travel_notification?.end_date
                      }
                    }}
                    {...field}
                  />
                )}
              />
            </FormControl>
          </div>
        </Stack>
        <div>
          <InputLabel htmlFor="locations-autocomplete">
            Location(s) of Travel *
          </InputLabel>
          <FormControl id="locations-autocomplete" fullWidth>
            <Controller
              control={control}
              defaultValue={[]}
              name="custom_data.travel_notification.locations"
              rules={{
                required: true
              }}
              render={({ field }) => (
                <Autocomplete
                  id="locations-autocomplete"
                  options={countryCodes}
                  autoHighlight
                  multiple
                  sx={{ mb: 2 }}
                  value={field.value}
                  onChange={(_, newValue) => field.onChange(newValue)}
                  getOptionLabel={(option) =>
                    countries[option]
                      ? `${option} - ${countries[option]} `
                      : option
                  }
                  isOptionEqualToValue={(option, value) => option === value}
                  renderInput={(params) => <TextField {...params} />}
                />
              )}
            />
          </FormControl>
        </div>
        <div>
          <InputLabel htmlFor="desired-action-radio-group" sx={{ mb: 1 }}>
            Desired Action *
          </InputLabel>
          <FormControl
            id="desired-action-radio-group"
            sx={{ ml: '0.75rem!important' }}
          >
            <Controller
              control={control}
              name="custom_data.travel_notification.desired_action"
              rules={{
                required: true
              }}
              render={({ field }) => (
                <RadioGroup
                  name="desired-action-radio-group"
                  value={field}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                  }}
                  sx={{ flexDirection: 'row', mb: 2, gap: 4 }}
                >
                  <FormControlLabel
                    value="suppress"
                    checked={field?.value === 'suppress'}
                    control={
                      <Radio
                        sx={{
                          color: errors.custom_data?.travel_notification
                            ?.desired_action
                            ? theme.vars.palette.error.main
                            : 'inherit',
                          marginTop: theme.spacing(-0.2)
                        }}
                      />
                    }
                    label="Suppress Alerts"
                  />
                  <FormControlLabel
                    value="raise"
                    checked={field?.value === 'raise'}
                    control={
                      <Radio
                        sx={{
                          color: errors.custom_data?.travel_notification
                            ?.desired_action
                            ? theme.vars.palette.error.main
                            : 'inherit',
                          marginTop: theme.spacing(-0.2)
                        }}
                      />
                    }
                    label="Raise Priority"
                  />
                </RadioGroup>
              )}
            />
          </FormControl>
        </div>
      </div>
    </>
  );
};

export default ServiceFormChangeRequestTravelNotification;
